import { useState } from "react";
import { SearchField } from "@/components/ui/search-field";
import { FieldLabel } from "@/components/ui/text";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/cms/ui/sheet";
import LureoButton from "@/common/LureoButton";
import { ListIcon } from "@phosphor-icons/react";
import lureoLogo from '@/assets/lureo_beta_logo_mixed.png';
import lureoLogoOnDark from '@/assets/Lureo_Beta_2.png';
import { Avatar } from '@/common/Avatar';
import { ThemedLogo, useThemeMode } from '@/modules/theme';

interface MobileNavProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filterStatus: 'all' | 'draft' | 'live' | 'archived' | 'pending';
  onFilterChange: (value: 'all' | 'draft' | 'live' | 'archived' | 'pending') => void;
  sortOrder: 'asc' | 'desc' | 'newest' | 'oldest';
  onSortChange: (value: 'asc' | 'desc' | 'newest' | 'oldest') => void;
  onMyAccount: () => void;
}

export function MobileNav({
  searchQuery,
  onSearchChange,
  filterStatus,
  onFilterChange,
  sortOrder,
  onSortChange,
  onMyAccount,
}: MobileNavProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { mode } = useThemeMode();
  return (
    <div className="lg:hidden">
      {/* Mobile/Tablet Header */}
      <div className="h-[4.25rem]" />
      <div className="fixed inset-x-0 top-0 z-lureo-layer-bar flex h-[4.25rem] items-center justify-between surface-bar p-4">
        <div className="relative">
          {/* Original logo */}
          <ThemedLogo src={mode === 'dark' ? lureoLogoOnDark : lureoLogo} alt="Lureo" className="h-8" />
          {/* White logo clipped to beta text area */}
        </div>
        <div className="flex items-center space-x-2">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <LureoButton variant="outline-2" aria-label="Open menu" suppressTooltip className="h-9 w-9 rounded-full p-0">
                <ListIcon className="h-4 w-4" />
              </LureoButton>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

             {/* My Account */}
             <div className="mt-4">
                  <LureoButton
                    variant="secondary"
                    onClick={() => {
                      onMyAccount();
                      setIsOpen(false);
                    }}
                    className="h-9 w-full rounded-lureo-radius-button-pill px-4"
                  >
                    <Avatar className="mr-2 h-5 w-5" size="sm" />
                    My Account
                  </LureoButton>
                </div>
              <div className="space-y-6 mt-8">
                {/* Search */}
                <div className="space-y-2">
                  <FieldLabel htmlFor="mobile-nav-search">Search Projects</FieldLabel>
                  <SearchField
                    id="mobile-nav-search"
                    size="md"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={onSearchChange}
                  />
                </div>

                {/* Filter */}
                <div className="space-y-2">
                  <FieldLabel>Filter by Status</FieldLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'draft', label: 'Draft' },
                      { value: 'live', label: 'Live' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'archived', label: 'Archived' },
                    ].map((option) => (
                      <LureoButton
                        key={option.value}
                        variant={filterStatus === option.value ? "solid" : "outline-3"}
                        aria-pressed={filterStatus === option.value}
                        onClick={() => {
                          onFilterChange(option.value as any);
                          setIsOpen(false);
                        }}
                        className="h-9 justify-start rounded-lureo-radius-button-pill px-4"
                      >
                        {option.label}
                      </LureoButton>
                    ))}
                  </div>
                </div>

                {/* Sort */}
                <div className="space-y-2">
                  <FieldLabel>Sort by</FieldLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'newest', label: 'Newest' },
                      { value: 'oldest', label: 'Oldest' },
                      { value: 'asc', label: 'A to Z' },
                      { value: 'desc', label: 'Z to A' },
                    ].map((option) => (
                      <LureoButton
                        key={option.value}
                        variant={sortOrder === option.value ? "solid" : "outline-3"}
                        aria-pressed={sortOrder === option.value}
                        onClick={() => {
                          onSortChange(option.value as any);
                          setIsOpen(false);
                        }}
                        className="h-9 justify-start rounded-lureo-radius-button-pill px-4"
                      >
                        {option.label}
                      </LureoButton>
                    ))}
                  </div>
                </div>

               
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
}